import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  Sparkles, Star, MapPin, ChevronRight, Loader2, 
  User, RotateCcw, Eye, Info, Flame, Shield, Award, Zap
} from 'lucide-react';
import { ReliabilityBadge, CommitmentBadge } from './ReliabilityBadge';
import { toast } from 'sonner';
import QuickBookModal from './QuickBookModal';

/**
 * SmartSuggestionsWidget - Shows intelligent talent recommendations for clients
 * 
 * Features:
 * - Previously Booked Talent (repeat booking convenience)
 * - Top Talent & High Commitment talent
 * - "Why this talent?" tooltips
 * - Quick Book / View Profile CTAs
 * - Direct Quick Book modal for one-click rebooking
 */

const TalentSuggestionCard = ({ talent, onViewProfile, onQuickBook, compact = false }) => {
  const [booking, setBooking] = useState(false);
  
  const handleQuickBook = async (e) => {
    e.stopPropagation();
    if (!talent.can_rebook) {
      onViewProfile(talent.talent_id);
      return;
    }
    setBooking(true);
    try {
      await onQuickBook(talent.talent_id, talent.display_name);
    } finally {
      setBooking(false);
    }
  };
  
  return (
    <Card 
      className={`group hover:shadow-md transition-all cursor-pointer border ${
        talent.suggestion_type === 'previously_booked' 
          ? 'border-amber-200 bg-gradient-to-br from-amber-50/50 to-white hover:border-amber-300'
          : 'border-cyan-200 bg-gradient-to-br from-cyan-50/30 to-white hover:border-cyan-300'
      }`}
      onClick={() => onViewProfile(talent.talent_id)}
      data-testid={`suggestion-card-${talent.talent_id}`}
    >
      <CardContent className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <Avatar className={compact ? 'w-10 h-10' : 'w-12 h-12'}>
            <AvatarImage src={talent.profile_photo_url} alt={talent.display_name} />
            <AvatarFallback className={
              talent.suggestion_type === 'previously_booked' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-cyan-100 text-cyan-700'
            }>
              {talent.first_name?.[0]}{talent.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-gray-900 text-sm truncate">
                {talent.display_name}
              </h4>
              
              {/* Suggestion Type Badge */}
              <Badge 
                variant="secondary" 
                className={`text-[10px] shrink-0 ${
                  talent.suggestion_type === 'previously_booked'
                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                    : 'bg-cyan-100 text-cyan-700 border-cyan-200'
                }`}
                data-testid="suggestion-type-badge"
              >
                {talent.suggestion_type === 'previously_booked' && (
                  <RotateCcw className="w-3 h-3 mr-1" />
                )}
                {talent.suggestion_type === 'recommended' && (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                {talent.suggestion_label}
              </Badge>
            </div>
            
            {/* Location & Rating */}
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
              {talent.city && talent.state && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {talent.city}, {talent.state}
                </span>
              )}
              {talent.global_rating > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span className="font-semibold text-gray-700">{talent.global_rating.toFixed(1)}</span>
                  {talent.total_reviews > 0 && (
                    <span className="text-gray-500">
                      ({talent.total_reviews} {talent.total_reviews === 1 ? 'review' : 'reviews'})
                    </span>
                  )}
                </span>
              )}
              {/* Iter-225v — rehire% trust signal */}
              {talent.rehire_pct != null && (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="talent-rehire-pct">
                  <RotateCcw className="w-3 h-3" />
                  {talent.rehire_pct}% rehire
                </span>
              )}
              {talent.booking_count > 0 && (
                <span className="text-amber-600">
                  {talent.booking_count} booking{talent.booking_count > 1 ? 's' : ''} with you
                </span>
              )}
            </div>

            {/* Iter-225v — latest review snippet (premium-marketplace social proof). */}
            {talent.latest_review_snippet && (
              <p className="mt-1.5 text-xs text-gray-600 italic line-clamp-2" data-testid="talent-review-snippet">
                “{talent.latest_review_snippet}”
              </p>
            )}
            
            {/* Reliability & Commitment Badges - Max 2 */}
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {talent.reliability_badge === 'top_talent' && (
                <ReliabilityBadge badge="top_talent" size="xs" showScore={false} showTooltip={true} />
              )}
              {talent.reliability_badge === 'reliable' && (
                <ReliabilityBadge badge="reliable" size="xs" showScore={false} showTooltip={true} />
              )}
              {(!talent.reliability_badge || talent.reliability_badge === 'new_talent') && (
                <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500 border-gray-200">
                  <User className="w-3 h-3 mr-1" />New
                </Badge>
              )}
              {talent.commitment_badge === 'high_commitment' && (
                <CommitmentBadge badge="high_commitment" size="xs" completed={talent.multiday_completed} />
              )}
            </div>
            
            {/* Why This Talent - Tooltip trigger */}
            {talent.why_this_talent?.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 mt-2 text-[11px] text-cyan-600 cursor-help hover:text-cyan-700">
                      <Info className="w-3 h-3" />
                      <span>Why this talent?</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs bg-slate-900 text-white p-3">
                    <p className="text-xs font-semibold mb-2 text-cyan-400">Why we recommend them:</p>
                    <div className="space-y-1">
                      {talent.why_this_talent.slice(0, 3).map((reason, idx) => (
                        <p key={idx} className="text-xs text-gray-200">• {reason}</p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile(talent.talent_id);
              }}
              data-testid={`view-profile-btn-${talent.talent_id}`}
            >
              <Eye className="w-3 h-3 mr-1" />
              View
            </Button>
            
            {talent.can_rebook && (
              <Button
                size="sm"
                className={`text-xs h-7 ${
                  talent.suggestion_type === 'previously_booked'
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                }`}
                onClick={handleQuickBook}
                disabled={booking}
                data-testid={`quick-book-btn-${talent.talent_id}`}
              >
                {booking ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-3 h-3 mr-1" />
                    Quick Book
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SmartSuggestionsWidget = ({ maxItems = 4, showHeader = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [previouslyBooked, setPreviouslyBooked] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [error, setError] = useState(null);
  
  // Quick Book modal state
  const [quickBookOpen, setQuickBookOpen] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  
  useEffect(() => {
    fetchSuggestions();
  }, []);
  
  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch previously booked talent
      const prevRes = await api.get('/suggestions/previously-booked?limit=6');
      setPreviouslyBooked(prevRes.data.talent || []);
      
      // Note: For recommended talent without a specific shift, we show the previously booked
      // The recommended-for-shift endpoint requires a shift_id
      // In a real scenario, you might also have a general "top talent" endpoint
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      setError('Unable to load recommendations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewProfile = (talentId) => {
    // Iter-227m — surface the AI Recommendation origin via query param
    // so TalentProfileDetail can tag the view.
    navigate(`/client/discover/${talentId}?source=ai_match`);
  };
  
  const handleQuickBook = async (talentId, talentName) => {
    // Find the talent data and open Quick Book modal
    const talent = [...previouslyBooked, ...recommended].find(t => t.talent_id === talentId);
    if (talent) {
      setSelectedTalent(talent);
      setQuickBookOpen(true);
    } else {
      // Fallback to old behavior
      toast.info(
        `To quickly rebook ${talentName}, go to an open shift and use Quick Fill`,
        {
          action: {
            label: 'View Shifts',
            onClick: () => navigate('/client/shifts')
          }
        }
      );
    }
  };
  
  const handleBookingComplete = (shiftId, talentId) => {
    // Refresh suggestions after booking
    fetchSuggestions();
  };
  
  // Combine and dedupe suggestions, prioritizing previously booked
  const allSuggestions = [
    ...previouslyBooked.map(t => ({ ...t, can_rebook: true })),
    ...recommended.filter(r => !previouslyBooked.find(p => p.talent_id === r.talent_id))
  ].slice(0, maxItems);
  
  if (loading) {
    return (
      <Card className="border-cyan-100">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }
  
  if (error || allSuggestions.length === 0) {
    return null; // Don't show widget if no suggestions
  }
  
  return (
    <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50/30 to-white" data-testid="smart-suggestions-widget">
      {showHeader && (
        <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Recommended For You
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/client/discover')}
            className="text-cyan-600 hover:text-cyan-700"
          >
            Browse All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
      )}
      
      <CardContent className={showHeader ? 'pt-0 px-4 pb-4' : 'p-4'}>
        {/* Previously Booked Section */}
        {previouslyBooked.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className="w-3.5 h-3.5 text-amber-500" />
              <h4 className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                Previously Worked With You
              </h4>
            </div>
            <div className="space-y-2">
              {previouslyBooked.slice(0, 2).map(talent => (
                <TalentSuggestionCard
                  key={talent.talent_id}
                  talent={{ ...talent, can_rebook: true }}
                  onViewProfile={handleViewProfile}
                  onQuickBook={handleQuickBook}
                  compact
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Top Talent Section - Show remaining from previously booked with good badges */}
        {previouslyBooked.filter(t => 
          t.reliability_badge === 'top_talent' || 
          t.reliability_badge === 'reliable' ||
          t.commitment_badge === 'high_commitment'
        ).length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-3.5 h-3.5 text-green-500" />
              <h4 className="text-xs font-medium text-green-700 uppercase tracking-wide">
                Proven Performers
              </h4>
            </div>
            <div className="space-y-2">
              {previouslyBooked
                .filter(t => 
                  t.reliability_badge === 'top_talent' || 
                  t.reliability_badge === 'reliable' ||
                  t.commitment_badge === 'high_commitment'
                )
                .slice(0, 2)
                .map(talent => (
                  <TalentSuggestionCard
                    key={`proven-${talent.talent_id}`}
                    talent={{
                      ...talent,
                      suggestion_type: 'recommended',
                      suggestion_label: talent.reliability_badge === 'top_talent' 
                        ? 'Top Talent' 
                        : talent.commitment_badge === 'high_commitment'
                          ? 'High Commitment'
                          : 'Reliable'
                    }}
                    onViewProfile={handleViewProfile}
                    onQuickBook={handleQuickBook}
                    compact
                  />
                ))}
            </div>
          </div>
        )}
        
        {/* Empty state if only new talent */}
        {previouslyBooked.length > 0 && 
         previouslyBooked.filter(t => 
           t.reliability_badge === 'top_talent' || 
           t.reliability_badge === 'reliable' ||
           t.commitment_badge === 'high_commitment'
         ).length === 0 && (
          <div className="text-center py-4 bg-gray-50 rounded-lg mt-2">
            <p className="text-xs text-gray-500">
              Book more talent to build your trusted network
            </p>
            <Button 
              size="sm" 
              variant="link" 
              className="text-xs text-cyan-600 mt-1"
              onClick={() => navigate('/client/discover')}
            >
              Discover Talent
            </Button>
          </div>
        )}
      </CardContent>
      
      {/* Quick Book Modal */}
      <QuickBookModal
        isOpen={quickBookOpen}
        onClose={() => {
          setQuickBookOpen(false);
          setSelectedTalent(null);
        }}
        talent={selectedTalent}
        onBookingComplete={handleBookingComplete}
      />
    </Card>
  );
};

export default SmartSuggestionsWidget;
